import React, { useEffect, useCallback } from "react";
import { HashRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { blue, indigo } from "@mui/material/colors";

// pages
import SearchForms from "./pages/SearchForms/SearchForms";
import EmployerFormDetails from "./pages/EmployerFormDetails/EmployerFormDetails";
import SearchEnroll from "./pages/SearchEnroll/SearchEnroll";
import SummaryPlanDescription from "./packages/data/components/SummaryPlanDescription";
import PayrollDates from "./packages/data/components/PayrollDates";
import ApprovedVendorsContainer from "./pages/ApprovedVendors/ApprovedVendorsContainer";

// components
import ScrollToTop from "./components/ScrollToTop";
import Version from "./components/Version";

// utils
import {
  isTrustedParentMessage,
  isSafeInternalRoute,
} from "./utils/iframeMessaging";

const theme = createTheme({
  palette: {
    secondary: { main: indigo[700] },
    primary: { main: blue[900] },
  },
  typography: {
    fontFamily: ['"Lato"', "sans-serif"].join(","),
  },
});

const defaultRoute = process.env.REACT_APP_DEFAULT_ROUTE === "forms" ? "/search-forms" : "/search-enroll";

// Component to handle iframe communication
function IframeCommunication() {
  const navigate = useNavigate();
  const location = useLocation();

  // Debug flag - set to true to enable console logs
  const DEBUG_ROUTING = false;
  
  // Check if we're in an iframe environment
  const isIframe = window.parent && window.parent !== window;
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  const debugLog = useCallback((message, data = null) => {
    if (DEBUG_ROUTING) {
      if (data) {
        console.log('React:', message, data);
      } else {
        console.log('React:', message);
      }
    }
  }, [DEBUG_ROUTING]);

  // Function to trigger external navigation
  const triggerExternalNavigation = useCallback((url) => {
    debugLog('Triggering external navigation to:', url);
    if (isIframe) {
      // In iframe: send message to parent
      window.parent.postMessage({
        type: 'EXTERNAL_NAVIGATE',
        url: url
      }, '*');
    } else {
      // Standalone: create a temporary anchor and click it
      // This approach is more reliable with popup blockers
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [debugLog, isIframe]);

  // Function to trigger external navigation using relius_web from API with form data
  const triggerReliusNavigation = useCallback((reliusWeb, formData = null) => {
    if (formData) {
      // Create and submit a form with the provided data
      const form = document.createElement('form');
      form.method = 'post';
      form.action = `https://${reliusWeb}/enrollinplan.aspx`;
      form.target = '_blank';
      
      // Add form fields
      Object.entries(formData).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });
      
      // Add form to document and submit
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
      
      debugLog('Submitted form to Relius with data:', { reliusWeb, formData });
    } else {
      // Fallback to direct navigation
      const url = `https://${reliusWeb}/enrollinplan.aspx`;
      debugLog('Triggering Relius navigation to:', url);
      triggerExternalNavigation(url);
    }
  }, [debugLog, triggerExternalNavigation]);

  // Function to handle external vendor links (Open Account links)
  const triggerExternalVendorLink = useCallback((url) => {
    debugLog('Triggering external vendor link:', url);
    
    if (isIframe) {
      // In iframe: send message to parent
      debugLog('Sending EXTERNAL_VENDOR_LINK message to parent:', url);
      window.parent.postMessage({
        type: 'EXTERNAL_VENDOR_LINK',
        url: url
      }, '*');
    } else {
      // Standalone: create a temporary anchor and click it
      // This approach is more reliable with popup blockers
      debugLog('Standalone mode, creating anchor link:', url);
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [debugLog, isIframe]);

  // Expose the functions globally so components can use them
  useEffect(() => {
    // Only expose iframe functions if we're in an iframe environment
    if (isIframe) {
      window.triggerExternalNavigation = triggerExternalNavigation;
      window.triggerReliusNavigation = triggerReliusNavigation;
      window.triggerExternalVendorLink = triggerExternalVendorLink;
    }
    return () => {
      delete window.triggerExternalNavigation;
      delete window.triggerReliusNavigation;
      delete window.triggerExternalVendorLink;
    };
  }, [triggerExternalNavigation, triggerReliusNavigation, triggerExternalVendorLink, isIframe]);

  useEffect(() => {
    debugLog('IframeCommunication component mounted');
    debugLog('Current location:', location.pathname);
    debugLog('Environment:', { isIframe, isLocalhost });

    // Only run iframe communication if we're in an iframe environment
    if (!isIframe) {
      debugLog('Not in iframe - skipping iframe communication');
      return;
    }

    // Request website info from parent when component mounts (only once)
    const requestWebsiteInfo = () => {
      if (window.parent && window.parent !== window) {
        debugLog('Requesting website info from parent');
        window.parent.postMessage({
          type: 'REQUEST_WEBSITE_INFO'
        }, '*');
      }
    };

    // Listen for messages from parent WordPress page
    const handleMessage = (event) => {
      debugLog('Received message from parent:', { data: event.data, origin: event.origin });
      
      // Only the page that actually embeds us may drive navigation. Origins are
      // pinned too when REACT_APP_ALLOWED_PARENT_ORIGINS is set - see
      // utils/iframeMessaging.
      if (!isTrustedParentMessage(event)) {
        debugLog('Ignoring message from untrusted source:', event.origin);
        return;
      }

      if (event.data && typeof event.data === 'object' && event.data.type === 'ROUTE_CHANGE') {
        const route = event.data.route;
        debugLog('Route change requested:', { route, current: location.pathname });
        // The route is attacker-controllable input, so it has to look like an
        // in-app path before it reaches the router.
        if (route && !isSafeInternalRoute(route)) {
          debugLog('Rejecting unsafe route:', route);
          return;
        }
        if (route && route !== location.pathname) {
          debugLog('Navigating to:', route);
          navigate(route);
        }
      } else {
        debugLog('Ignoring non-route message:', event.data);
      }
    };



    // Request website info whenever the component mounts (for any route)
    requestWebsiteInfo();

    // Add event listeners
    window.addEventListener('message', handleMessage);

    // Cleanup
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [navigate, debugLog, location.pathname, isIframe, isLocalhost]);

  // Send route to parent whenever location changes
  useEffect(() => {
    const sendRouteToParent = () => {
      if (window.parent && window.parent !== window) {
        debugLog('Sending route to parent:', location.pathname);
        window.parent.postMessage({
          type: 'NAVIGATE',
          route: location.pathname
        }, '*');
      }
    };

    sendRouteToParent();
  }, [location.pathname, debugLog]);

  return null;
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <IframeCommunication />
        <ScrollToTop>
          <Routes>
            <Route path="/search-forms/:employerId/:planId/spd" element={<SummaryPlanDescription />} />
            <Route path="/search-forms/:employerId/:planId/payroll" element={<PayrollDates />} />
            <Route path="/search-forms/:employerId/:empState/approved-vendor/:planType" element={<ApprovedVendorsContainer />} />
            <Route path="/search-forms/:employerId" element={<EmployerFormDetails />} />
            <Route path="/search-forms" element={<SearchForms />} />
            <Route path="/search-enroll/:employerId/:empState/approved-vendor/:planType" element={<ApprovedVendorsContainer />} />
            <Route path="/search-enroll/:employerId" element={<SearchEnroll />} />
            <Route path="/search-enroll" element={<SearchEnroll />} />

            {/* Dynamically set the default redirect based on the environment variable */}
            <Route path="*" element={<Navigate to={defaultRoute} replace />} />
          </Routes>
          <Version />
        </ScrollToTop>
      </Router>
    </ThemeProvider>
  );
}

export default App;
